import { observable, action, makeObservable } from 'mobx';
import * as yup from 'yup';
import { RootStore } from './root';
import { MomentSchema } from '../utils/yup-schemas';
import { WithNetworkConcurrency } from './with-network-concurrency';
import { NotificationManager } from '../components/notification-manager';
import moment from 'moment';
import { HttpMethod, ConfirmOutcome } from '../types';
import { ApplicationTargetEnum, AppTarget } from '../types/app-selection';
import type { PromotionGrantType, PromotionStatus } from '../types/promotion';

const optionalString = () => yup.string().notRequired().nullable();
const optionalNumber = () => yup.number().notRequired().nullable();
const nullableDate = new MomentSchema().nullable();

export interface PhoneValidationCode {
    id: number;
    confirmationCode: string;
    createdAt: string;
    messageId?: string;
}

export interface SMSDeliveryStatus {
    id: number;
    status: string | null;
    clientId: number;
    provider: string | null;
    providerResponse: string | null;
    createdAt: string;
}

export interface PaginatedPhoneValidationCodes {
    total: number;
    page: number;
    perPage: number;
    results: PhoneValidationCode[];
}

export interface UserPhoneValidated {
    id: number;
    phoneNumber: string | null;
    phoneValidatedAt: string | null;
}

export interface PhoneValidationResult {
    status: 'validated' | 'already_validated';
    message: string;
    invalidatedCount?: number;
}

// Mirror of AgeVerificationStatus enum in ctd-web-api (src/models/domain/age-verification.ts)
export enum AgeVerificationStatus {
    VERIFIED = 'verified',
    INSUFFICIENT = 'insufficient',
    UNVERIFIED = 'unverified',
}

export interface AgeVerificationHistoryEntry {
    type: 'age_verification' | 'interac_session';
    occurredAt: string;
    outcome: 'success' | 'failure';
    reason: string | null;
    province: string | null;
}

export interface ClientExternalDetails {
    id: number;
    ngrpCustomerId: string;
    liquidBarcodeId: string | null;
    email: string;
    ageVerification?: {
        status: AgeVerificationStatus;
        maxAgeVerified: number;
        provinceThreshold: number;
        province: string;
        lastVerifiedAt: string;
    } | null;
}

export interface SwapClientDetails {
    client: {
        email: string;
        ngrpCustomerId: string;
        liquidBarcodeId: string;
        id: number;
    };
    otherAccount: {
        email: string;
        ngrpCustomerId: string;
        liquidBarcodeId: string;
        id: number;
    };
}

const washCodeRecordSchema = yup.object({
    id: yup.number().required(),
    washCode: yup.string().nullable(),
    store: yup.string().nullable(),
    successCode: yup.string().nullable(),
    rejectMessage: yup.string().nullable(),
    createdAt: new MomentSchema().nullable(),
});

const paginatedWashCodeRecordSchema = yup.object({
    page: yup.number().required(),
    perPage: yup.number().required(),
    total: yup.number().required(),
    results: yup.array(washCodeRecordSchema).default([]),
});

export type PaginatedWashCodeRecordList = yup.InferType<
    typeof paginatedWashCodeRecordSchema
>;

const consentStatusSchema = yup.object({
    id: yup.number().required(),
    type: yup.string().required(),
    linkText: yup.string().required(),
    status: yup.string().nullable(),
    createdAt: yup.string().notRequired(),
});

type ConsentStatus = yup.InferType<typeof consentStatusSchema>;
export type UserConsent = ConsentStatus;

const clientSchema = yup.object({
    id: yup.number().required(),
    firstName: yup.string().notRequired(),
    lastName: yup.string().notRequired(),
    email: optionalString(),
    picture: optionalString(),
    language: optionalString().matches(/en|fr/),
    facebookId: optionalString(),
    liquidBarcodeId: optionalString(),
    ngrpCustomerId: optionalString(),
    appleId: optionalString(),
    googleId: optionalString(),
    birthday: nullableDate,
    gender: optionalString().matches(/male|female/),
    phoneNumber: optionalString(),
    postalCode: optionalString(),
    carwashDeviceLockedUntil: optionalString(),
    carwashSubscriptionDevice: optionalString(),
    dateCreated: nullableDate,
    dateUpdated: nullableDate,
    lastSessionAt: nullableDate,
    lockedAt: nullableDate,
    invalidLoginCount: optionalNumber(),
    isEmailConfirmed: yup.boolean().notRequired().nullable(),
    isPhoneNumberConfirmed: yup.boolean().notRequired().nullable(),
    ageVerificationStatus: optionalString() as yup.StringSchema<
        AgeVerificationStatus | null | undefined
    >,
    maxAgeVerified: optionalNumber(),
    verifiedInProvince: optionalString(),
    province: optionalString(),
    lastVerifiedAt: optionalString(),
    deletedAt: new MomentSchema().nullable(),
    appTargets: yup.array(yup.string().required()).default([]),
    isTester: yup.boolean().notRequired().nullable(),
    ageVerificationHistory: yup
        .array()
        .of(
            yup.object({
                type: yup.string().required() as yup.StringSchema<
                    'age_verification' | 'interac_session'
                >,
                occurredAt: yup.string().required(),
                outcome: yup.string().required() as yup.StringSchema<
                    'success' | 'failure'
                >,
                reason: yup.string().nullable().defined(),
                province: yup.string().nullable().defined(),
            })
        )
        .default([]),
});

const paginatedListSchema = yup.object({
    page: yup.number().required(),
    perPage: yup.number().required(),
    total: yup.number().required(),
    results: yup.array(clientSchema).default([]),
});

export type Client = yup.InferType<typeof clientSchema>;
export type PaginatedClientList = yup.InferType<typeof paginatedListSchema>;

export type SearchParams = {
    perPage?: number;
    page?: number;
    q?: string;
};

export type FetchListParams = SearchParams & {
    appTarget?: AppTarget;
};

export interface PaymentMethod {
    defaultPayment: string;
}

export interface DeviceAppVersion {
    appVersion: string | null;
    os: string | null;
}

export type PaymentMethodQuery = {
    clientId: number;
};

export interface ClientCoupon {
    id: number;
    promotionId: number;
    title: string | null;
    thumbnail: string | null;
    createdAt: string;
    expireAt?: string | null;
    grantType?: PromotionGrantType;
    timezone?: string | null;
    status?: PromotionStatus | null;
    updatedAt?: string;
    deletedAt?: string | null;
}

export interface PaginatedClientCoupons {
    page: number;
    perPage: number;
    total: number;
    results: ClientCoupon[];
}

export const CAMPAIGN_ACTIVITY_TYPES = [
    'visit',
    'join',
    'milestone_reached',
    'coupon_awarded',
    'coupon_redeemed',
] as const;

export type CampaignActivityType = typeof CAMPAIGN_ACTIVITY_TYPES[number];

/** The rules engine's own keys, not the wording the app shows a member. */
export type VisitRejectionReason =
    | 'min_spend_not_met'
    | 'gap_too_short'
    | 'daily_cap_reached'
    | 'unparseable_phone'
    | 'campaign_ended';

export type VisitSource = 'LIFT' | 'NPE' | 'ENROLLMENT' | 'CS_GRANT';

export interface CampaignActivityVisit {
    countedDelta: 0 | 1;
    totalPrice: number | null;
    source: VisitSource;
    rejectionReason: VisitRejectionReason | null;
    rejectionLabel: string | null;
    store: { street: string; city: string } | null;
    csGrant: {
        agentEmail: string;
        note: string | null;
        grantedAt: string;
    } | null;
}

export interface CampaignActivityItem {
    id: string;
    type: CampaignActivityType;
    occurredAt: string;
    visit: CampaignActivityVisit | null;
    milestone: { position: number; threshold: number } | null;
    coupon: {
        promotionId: number | null;
        title: string | null;
        description: string | null;
        thumbnail: string | null;
    } | null;
}

export interface ManualVisitResult {
    visitId: number;
    occurredAt: string;
    /** The client's counted-visit total after the grant. */
    countedVisits: number;
}

export interface PaginatedCampaignActivity {
    page: number;
    perPage: number;
    total: number;
    /** The visit ledger read cap cut the history short, so `total` understates it. */
    truncated: boolean;
    results: CampaignActivityItem[];
}

/** A club whose campaign activity log can be read: nothing of its configuration. */
export interface CampaignActivityClub {
    id: number;
    name: string;
}

export type CouponSide = 'issued' | 'redeemed';
export type CouponCampaign = 'full_app' | 'rpp';

const nhlGamePinsSchema = yup.object({
    pins: yup.array(yup.string().required()),
});

const nhlGameProfilSchema = yup.object({
    participationId: yup.number().required(),
    nhlGameId: yup.number(),
    userId: yup.number(),
    createdAt: new MomentSchema().notRequired().nullable(),
    team: yup.string().nullable(),
    teamLogo: yup.string().nullable(),
    points: yup.number().nullable(),
    rank: yup.number().nullable(),
    pins: yup.object({
        totalPins: yup.number().nullable(),
        totalPinsPending: yup.number().nullable(),
        totalPinsValidated: yup.number().nullable(),
        totalPinsBurned: yup.number().nullable(),
        totalPinsSac: yup.number().nullable(),
    }),
    predictions: yup.object({
        totalPredictions: yup.number().nullable(),
        predictionWithPins: yup.number().nullable(),
        predictionWithoutPins: yup.number().nullable(),
    }),
});

const unblockPaymentMethodSchema = yup.object().shape({
    status: yup.string().oneOf(['unblocked', 'already_unblocked']).required(),
    unblockUntil: yup.string().nullable().required(),
});

export type NhlGameProfil = yup.InferType<typeof nhlGameProfilSchema>;

export type NhlGamePins = yup.InferType<typeof nhlGamePinsSchema>;

export type UnblockPaymentSchema = yup.InferType<
    typeof unblockPaymentMethodSchema
>;

const onboardingStepSchema = yup.object({
    id: yup.number().required(),
    action: yup.string().required(),
    stepStatus: yup.string().required(),
    userResponse: yup.string().nullable(),
    stepDoneAt: new MomentSchema().nullable(),
    stepAssociatedApp: yup.string().nullable(),
});

const onboardingProgressionSchema = yup.object({
    [ApplicationTargetEnum.CA]: yup
        .string()
        .oneOf(['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED']),
    [ApplicationTargetEnum.QC]: yup
        .string()
        .oneOf(['NOT_STARTED', 'IN_PROGRESS', 'COMPLETED']),
});

const onboardingStepsSchema = yup.object({
    onboardingProgression: onboardingProgressionSchema.required(),
    hasOnboardingProgression: yup.boolean().required(),
    steps: yup.array(onboardingStepSchema).default([]),
});

export type OnboardingSteps = yup.InferType<typeof onboardingStepsSchema>;

export class ClientStore extends WithNetworkConcurrency {
    @observable fetchingCount = 0;
    @observable list: PaginatedClientList | null = null;
    @observable isSendingResetEmail = false;
    @observable isUnblockingDevice = false;
    @observable paymentMethod: string | null = null;
    @observable isFetchingPaymentMethod = false;
    @observable isSendingValidationEmail = false;
    @observable isReissuingPromotion = false;
    @observable isFetchingNhlGameProfil = false;
    @observable isFetchingNhlGameProfilPins = false;
    @observable isUnblockingPaymentMethod = false;
    @observable nhlGameProfil: NhlGameProfil | null = null;
    @observable nhlGamePins: NhlGamePins | null = null;
    @observable isDeletingClient = false;
    @observable deviceAppVersion: DeviceAppVersion | null = null;
    @observable isFetchingDeviceAppVersion = false;
    @observable isLoading = false;
    @observable clientDetails: ClientExternalDetails | null = null;
    @observable userConsentsWithStatus: ConsentStatus[] | null = null;
    @observable isFetchingUserConsents = false;
    @observable isUpdatingConsentStatus = false;
    @observable washCodeRecords: PaginatedWashCodeRecordList | null = null;
    @observable isFetchingWashCodeRecords = false;
    @observable onboardingSteps: OnboardingSteps | null = null;
    @observable isFetchingOnboardingSteps = false;
    @observable onboardingProgression: Record<string, string> | null = null;
    /**
     * The client the loaded onboarding data belongs to, or null when nothing is
     * loaded. A reader must check this before drawing conclusions from
     * `onboardingSteps`: those fields survive a client change and a failed
     * fetch, so "no steps" otherwise reads the same as "not loaded".
     */
    @observable onboardingStepsClientId: number | null = null;
    // Separate from the inherited list tag: sharing it would let this fetch
    // drop the client list's in-flight response.
    private onboardingStepsTag = Symbol();
    @observable isRestartingOnboarding = false;
    @observable isCompletingOnboarding = false;
    @observable
    phoneValidationCodes: PaginatedPhoneValidationCodes | null = null;
    @observable phoneValidatedUsers: UserPhoneValidated[] = [];
    @observable isFetchingPhoneValidationCodes = false;
    @observable isFetchingPhoneValidatedUsers = false;
    @observable isValidatingPhoneNumber = false;
    @observable smsDeliveryStatus: SMSDeliveryStatus | null = null;
    @observable isFetchingSMSDeliveryStatus = false;
    @observable isValidatingClientEmail = false;
    @observable isResettingAgeVerification = false;
    @observable isUpdatingTesterStatus = false;

    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        super();
        makeObservable(this);
        this.rootStore = rootStore;
    }

    @action
    async fetchList(this: ClientStore, params: FetchListParams) {
        const tag = this.getTag();
        this.fetchingCount++;
        const response = await this.rootStore.makeNetworkCall(
            {
                method: HttpMethod.GET,
                url: 'clients',
                params,
            },
            paginatedListSchema
        );
        this.fetchingCount--;

        if (response.data && this.isLatestTag(tag)) {
            this.list = response.data;
        }
    }

    @action
    async sendResetPasswordEmail(this: ClientStore, email: string) {
        this.isSendingResetEmail = true;
        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.POST,
            url: 'clients/reset-password',
            data: {
                email,
            },
        });
        this.isSendingResetEmail = false;
        if (!response.err) {
            NotificationManager.showSuccess('Reset password email sent');
        }
    }

    @action
    async unblockCarwashDevice(this: ClientStore, clientId: number) {
        this.isUnblockingDevice = true;
        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.POST,
            url: 'clients/unblock-carwash',
            data: {
                clientId,
            },
        });
        this.isUnblockingDevice = false;
        if (!response.err) {
            NotificationManager.showSuccess('User can now claim a new device');
        }
    }

    @action
    async getPaymentMethod(this: ClientStore, params: PaymentMethodQuery) {
        this.isFetchingPaymentMethod = true;
        const response = await this.rootStore.makeNetworkCall<PaymentMethod>({
            method: HttpMethod.GET,
            url: 'clients/payment-method',
            params,
        });
        this.isFetchingPaymentMethod = false;
        if (!response.err) {
            this.paymentMethod = response.data.defaultPayment;
        }
    }

    @action
    async unblockPaymentMethod(this: ClientStore, clientId: number) {
        this.isUnblockingPaymentMethod = true;
        const response = await this.rootStore.makeNetworkCall<
            UnblockPaymentSchema
        >({
            method: HttpMethod.POST,
            url: `clients/${clientId}/payment-method/unblock`,
        });
        this.isUnblockingPaymentMethod = false;
        if (!response.err) {
            const date = moment(new Date(response.data.unblockUntil)).format(
                'LLL'
            );

            const statusMessage =
                response.data.status === 'unblocked'
                    ? `User's payment method has been unblocked until ${date}.`
                    : `User's payment method was already unblocked until ${date}.`;

            NotificationManager.showSuccess(statusMessage);
        }
    }

    @action
    async validateEmail(this: ClientStore, clientId: number) {
        this.isSendingValidationEmail = true;
        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.POST,
            url: 'clients/validate-email',
            data: {
                clientId,
            },
        });
        this.isSendingValidationEmail = false;
        if (!response.err) {
            NotificationManager.showSuccess('Email validation email sent');
        }
    }

    // No store field or shared tag guard: this tab can mount twice at once,
    // and reusing fetchList's tag would silently drop responses whenever a
    // clients-list fetch is also in flight.
    @action
    async fetchClientCoupons(
        this: ClientStore,
        params: {
            clientId: number;
            side: CouponSide;
            campaign: CouponCampaign;
            page: number;
            perPage: number;
            q?: string;
        }
    ): Promise<PaginatedClientCoupons | null> {
        const suffix = params.campaign === 'rpp' ? '-rpp' : '';

        const response = await this.rootStore.makeNetworkCall<
            PaginatedClientCoupons
        >({
            method: HttpMethod.GET,
            url: `clients/${params.clientId}/coupons/${params.side}${suffix}`,
            params: {
                page: params.page,
                perPage: params.perPage,
                ...(params.q ? { q: params.q } : {}),
            },
        });

        return response.err ? null : response.data;
    }

    /** Null when the request failed; an empty list when no club qualifies. */
    @action
    async fetchCampaignActivityClubs(
        this: ClientStore
    ): Promise<CampaignActivityClub[] | null> {
        const response = await this.rootStore.makeNetworkCall<{
            results: CampaignActivityClub[];
        }>({
            method: HttpMethod.GET,
            url: 'clients/campaign-activity/clubs',
        });

        return response.err ? null : response.data.results;
    }

    // Same reasoning as fetchClientCoupons: no store field and no shared tag,
    // since the profile drawer keeps every tab pane mounted at once.
    @action
    async fetchCampaignActivity(
        this: ClientStore,
        params: {
            clientId: number;
            clubId: number;
            types: readonly CampaignActivityType[];
            sort: 'asc' | 'desc';
            page: number;
            perPage: number;
        }
    ): Promise<PaginatedCampaignActivity | null> {
        const response = await this.rootStore.makeNetworkCall<
            PaginatedCampaignActivity
        >({
            method: HttpMethod.GET,
            url: `clients/${params.clientId}/campaign-activity`,
            params: {
                clubId: params.clubId,
                sort: params.sort,
                page: params.page,
                perPage: params.perPage,
                // Omitted rather than sent empty: the endpoint reads "no types"
                // as "every type", and an empty array serialises to nothing
                // useful anyway.
                ...(params.types.length > 0
                    ? { types: params.types.join(',') }
                    : {}),
            },
        });

        return response.err ? null : response.data;
    }

    @action
    async addCampaignVisit(
        this: ClientStore,
        params: {
            clientId: number;
            clubId: number;
            occurredAt: string;
            note: string | null;
            requestKey: string;
        }
    ): Promise<ManualVisitResult | null> {
        const response = await this.rootStore.makeNetworkCall<
            ManualVisitResult
        >({
            method: HttpMethod.POST,
            url: `clients/${params.clientId}/campaign-activity/visits`,
            data: {
                clubId: params.clubId,
                occurredAt: params.occurredAt,
                requestKey: params.requestKey,
                ...(params.note ? { note: params.note } : {}),
            },
        });

        return response.err ? null : response.data;
    }

    @action
    async reissuePromotion(
        this: ClientStore,
        clientId: number,
        activationPromotionId: number
    ) {
        this.isReissuingPromotion = true;
        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.POST,
            url: 'clients/reissue-promotion',
            data: {
                clientId,
                activationPromotionId,
            },
        });
        this.isReissuingPromotion = false;
        if (!response.err) {
            NotificationManager.showSuccess('Coupon has been reissued');
        }
    }

    @action
    async getNhlGameProfil(
        this: ClientStore,
        params: { clientId: number }
    ): Promise<boolean> {
        this.isFetchingNhlGameProfil = true;
        const response = await this.rootStore.makeNetworkCall<NhlGameProfil>({
            method: HttpMethod.GET,
            url: 'clients/nhl-game-profil',
            params,
        });
        this.isFetchingNhlGameProfil = false;
        if (!response.err) {
            this.nhlGameProfil = response.data;

            return true;
        }

        return false;
    }

    @action
    async createSacPins(
        this: ClientStore,
        clientId: number,
        count: number
    ): Promise<NhlGamePins | null> {
        this.isFetchingNhlGameProfilPins = true;

        const response = await this.rootStore.makeNetworkCall<NhlGamePins>({
            method: HttpMethod.POST,
            url: 'clients/nhl-game-pins',
            data: {
                clientId,
                count,
            },
        });
        this.isFetchingNhlGameProfilPins = false;
        if (!response.err) {
            this.nhlGamePins = response.data;

            return this.nhlGamePins;
        }

        return null;
    }

    @action
    async deleteClient(clientId: number): Promise<ConfirmOutcome> {
        this.isDeletingClient = true;
        try {
            // makeNetworkCall never throws (root.ts catches internally and
            // resolves { err: true }), so the result has to be read rather than
            // caught, or a refusal reads as a success.
            const response = await this.rootStore.makeNetworkCall({
                method: HttpMethod.DELETE,
                url: `clients/${clientId}`,
            });

            // No toast here: makeNetworkCall raises one, and the message is
            // returned so the dialog can name the reason too. A refusal such as
            // a live carwash subscription is the agent's next action, and the
            // toast is gone by the time they read the dialog.
            return { ok: !response.err, message: response.message };
        } finally {
            this.isDeletingClient = false;
        }
    }

    @action
    async updateClientGrant(
        clientId: number,
        grantId: number,
        timezone: string,
        expireAt: string
    ): Promise<boolean> {
        // makeNetworkCall never throws (root.ts catches internally and
        // resolves { err: true }): check response.err rather than try/catch.
        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.PATCH,
            url: `clients/${clientId}/grants/${grantId}`,
            data: { expireAt, timezone },
        });

        if (response.err) {
            NotificationManager.showError(
                'An error occurred while updating rewards.'
            );
            return false;
        }

        NotificationManager.showSuccess(
            'The expiration date has been successfully updated.'
        );
        return true;
    }

    @action
    async fetchClientDeviceAppVersion(this: ClientStore, clientId: number) {
        this.isFetchingDeviceAppVersion = true;
        const response = await this.rootStore.makeNetworkCall<DeviceAppVersion>(
            {
                method: HttpMethod.GET,
                url: `clients/${clientId}/device-app-version`,
            }
        );
        this.isFetchingDeviceAppVersion = false;
        if (!response.err) {
            this.deviceAppVersion = {
                appVersion: response.data.appVersion,
                os: response.data.os,
            };
        }
    }

    @action
    async fetchClientDetails(clientId: number) {
        this.isLoading = true;
        const response = await this.rootStore.makeNetworkCall<
            ClientExternalDetails
        >({
            method: HttpMethod.GET,
            url: `clients/${clientId}/details`,
        });
        this.isLoading = false;

        if (!response.err) {
            this.clientDetails = response.data;
        } else {
            this.clientDetails = null;
            NotificationManager.showError('Failed to fetch client details');
        }
    }

    @action
    async updateExternalIdentifiers(
        clientId: number,
        data: { sourceAccountId: number }
    ) {
        const response = await this.rootStore.makeNetworkCall<
            SwapClientDetails
        >({
            method: HttpMethod.PATCH,
            url: `clients/${clientId}/external-identifiers`,
            data,
        });

        if (!response.err) {
            NotificationManager.showSuccess(
                'External identifiers updated successfully'
            );
            return response.data;
        } else {
            NotificationManager.showError(
                'Failed to update external identifiers'
            );
            return null;
        }
    }

    @action
    async fetchUserActiveConsentsWithStatus(clientId: number) {
        this.isFetchingUserConsents = true;
        const response = await this.rootStore.makeNetworkCall<ConsentStatus[]>({
            method: HttpMethod.GET,
            url: `clients/${clientId}/consents`,
        });

        this.isFetchingUserConsents = false;
        if (!response.err) {
            this.userConsentsWithStatus = response.data;
        } else {
            NotificationManager.showError(
                'Failed to fetch user consent statuses'
            );
            this.userConsentsWithStatus = null;
        }
    }

    @action
    async updateUserConsentStatus(
        clientId: number,
        userConsentRecordId: number,
        status: string
    ) {
        this.isUpdatingConsentStatus = true;
        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.PATCH,
            url: `clients/${clientId}/consents/${userConsentRecordId}`,
            data: { status },
        });

        this.isUpdatingConsentStatus = false;
        if (!response.err) {
            NotificationManager.showSuccess(
                'Consent status updated successfully'
            );
            await this.fetchUserActiveConsentsWithStatus(clientId);
        } else {
            NotificationManager.showError('Failed to update consent status');
        }
    }

    @action
    async fetchWashCodeRecords(clientId: number, page = 0, perPage = 10) {
        this.isFetchingWashCodeRecords = true;

        const response = await this.rootStore.makeNetworkCall<
            PaginatedWashCodeRecordList
        >(
            {
                method: HttpMethod.GET,
                url: `clients/${clientId}/car-wash/records`,
                params: { page, perPage },
            },
            paginatedWashCodeRecordSchema
        );

        this.isFetchingWashCodeRecords = false;
        if (!response.err) {
            this.washCodeRecords = response.data;
        } else {
            this.washCodeRecords = null;
            NotificationManager.showError('Failed to fetch wash code records');
        }
    }

    @action
    async fetchOnboardingSteps(clientId: number) {
        const tag = Symbol();
        this.onboardingStepsTag = tag;
        this.isFetchingOnboardingSteps = true;
        // Cleared up front so a reader cannot mistake the previous client's
        // data for this one's while the request is in flight.
        this.onboardingStepsClientId = null;

        const response = await this.rootStore.makeNetworkCall<OnboardingSteps>(
            {
                method: HttpMethod.GET,
                url: `clients/${clientId}/onboarding/progression`,
            },
            onboardingStepsSchema
        );

        // A newer fetch owns the spinner and the data; this reply is stale.
        if (tag !== this.onboardingStepsTag) {
            return;
        }

        this.isFetchingOnboardingSteps = false;
        if (!response.err) {
            this.onboardingSteps = response.data;
            this.onboardingProgression = response.data.onboardingProgression;
            this.onboardingStepsClientId = clientId;
        } else {
            this.onboardingSteps = null;
            this.onboardingProgression = null;
            NotificationManager.showError('Failed to fetch onboarding steps');
        }
    }

    /**
     * Marks a client's onboarding finished. For a client who started one and is
     * stuck partway, which is the action this button has always performed.
     * `restartOnboarding` is the other half, for legacy clients with no
     * progression at all.
     */
    @action
    async completeOnboarding(clientId: number): Promise<boolean> {
        this.isCompletingOnboarding = true;
        try {
            const response = await this.rootStore.makeNetworkCall({
                method: HttpMethod.POST,
                url: `clients/${clientId}/onboarding/complete`,
            });

            if (!response.err) {
                NotificationManager.showSuccess(
                    'Onboarding completed successfully'
                );

                await this.fetchOnboardingSteps(clientId);
                return true;
            }

            // makeNetworkCall has already surfaced the server's own message.
            return false;
        } finally {
            this.isCompletingOnboarding = false;
        }
    }

    @action
    async restartOnboarding(
        clientId: number,
        appTarget: ApplicationTargetEnum
    ): Promise<boolean> {
        this.isRestartingOnboarding = true;
        try {
            const response = await this.rootStore.makeNetworkCall({
                method: HttpMethod.POST,
                url: `clients/${clientId}/onboarding/restart`,
                data: { appTarget },
            });

            if (!response.err) {
                NotificationManager.showSuccess(
                    'Onboarding restarted. The client will be prompted to complete their profile on next app open.'
                );

                await this.fetchOnboardingSteps(clientId);
                return true;
            }

            // makeNetworkCall has already surfaced the server's own message.
            // Adding a second generic toast here only buried it.
            return false;
        } finally {
            this.isRestartingOnboarding = false;
        }
    }

    @action
    async fetchPhoneValidationCodes(clientId: number, limit = 3, offset = 0) {
        this.isFetchingPhoneValidationCodes = true;
        const response = await this.rootStore.makeNetworkCall<
            PaginatedPhoneValidationCodes
        >({
            method: HttpMethod.GET,
            url: `clients/${clientId}/phone-validation-codes`,
            params: { limit, offset },
        });
        this.isFetchingPhoneValidationCodes = false;
        if (!response.err) {
            this.phoneValidationCodes = response.data;
        } else {
            NotificationManager.showError('Failed to fetch validation codes');
        }
    }

    @action
    async fetchPhoneValidatedUsers(clientId: number) {
        this.isFetchingPhoneValidatedUsers = true;
        const response = await this.rootStore.makeNetworkCall<
            UserPhoneValidated[]
        >({
            method: HttpMethod.GET,
            url: `clients/${clientId}/phone-validated-users`,
        });
        this.isFetchingPhoneValidatedUsers = false;
        if (!response.err) {
            this.phoneValidatedUsers = response.data;
        } else {
            NotificationManager.showError('Failed to fetch validated users');
        }
    }

    @action
    async validatePhoneNumber(
        clientId: number
    ): Promise<PhoneValidationResult | null> {
        this.isValidatingPhoneNumber = true;
        const response = await this.rootStore.makeNetworkCall<
            PhoneValidationResult
        >({
            method: HttpMethod.PATCH,
            url: `clients/${clientId}/phone-number/validate`,
        });
        this.isValidatingPhoneNumber = false;
        if (!response.err) {
            NotificationManager.showSuccess(response.data.message);

            await this.fetchPhoneValidatedUsers(clientId);
            return response.data;
        } else {
            NotificationManager.showError('Failed to validate phone number');
            return null;
        }
    }

    @action
    async fetchSMSDeliveryStatus(messageId: string) {
        this.isFetchingSMSDeliveryStatus = true;
        this.smsDeliveryStatus = null;

        const response = await this.rootStore.makeNetworkCall<
            SMSDeliveryStatus
        >({
            method: HttpMethod.GET,
            url: `clients/sms/${messageId}/delivery-status`,
        });

        this.isFetchingSMSDeliveryStatus = false;
        if (!response.err) {
            this.smsDeliveryStatus = response.data;
            return response.data;
        } else {
            NotificationManager.showError(
                'Failed to fetch SMS delivery status'
            );
            return null;
        }
    }

    @action
    async resetAgeVerification(clientId: number): Promise<void> {
        this.isResettingAgeVerification = true;
        try {
            await this.rootStore.makeNetworkCall({
                method: HttpMethod.DELETE,
                url: 'age-verification',
                data: { clientId },
            });
        } finally {
            this.isResettingAgeVerification = false;
        }
    }

    @action
    async validateClientEmail(
        this: ClientStore,
        clientId: number
    ): Promise<{ status: string; message: string } | null> {
        this.isValidatingClientEmail = true;
        const response = await this.rootStore.makeNetworkCall<{
            status: string;
            message: string;
        }>({
            method: HttpMethod.PATCH,
            url: `clients/${clientId}/email/validate`,
        });
        this.isValidatingClientEmail = false;
        if (!response.err) {
            NotificationManager.showSuccess(response.data.message);
            return response.data;
        } else {
            NotificationManager.showError('Failed to validate client email');
            return null;
        }
    }

    @action
    async updateTesterStatus(
        this: ClientStore,
        clientId: number,
        isTester: boolean
    ): Promise<void> {
        this.isUpdatingTesterStatus = true;
        const response = await this.rootStore.makeNetworkCall({
            method: HttpMethod.PATCH,
            url: `clients/${clientId}/tester-status`,
            data: { isTester },
        });
        this.isUpdatingTesterStatus = false;
        if (!response.err) {
            NotificationManager.showSuccess(
                `Tester status ${
                    isTester ? 'enabled' : 'disabled'
                } successfully`
            );
        } else {
            NotificationManager.showError('Failed to update tester status');
        }
    }
}
